// Loads analytics and marketing tags only as the visitor's consent allows
// (spec 2026-09-14-meta-ads-end-to-end-tracking-design §4.1, §4.3).
// Consent regions start with everything off until the banner choice; other
// regions start from the default record. Marketing is gated on the derived
// ad consent (GPC included), not the raw marketing flag. Granting a category
// applies immediately with no reload; but Ahrefs, Rewardful and GA4 can't be
// unloaded once they've pinged, so withdrawing a category that was already
// loaded reloads the page once so nothing keeps running.
import { META_PIXEL_ID, isPricingPath, isRedirectPath } from '~/utils/tracking/metaEvents';
import { injectGoogleTag, sendGoogleTagToWorker } from '~/utils/tracking/googleTagWorker';
import { createGoogleTagLoader } from '~/utils/tracking/googleTag';
import { GOOGLE_ADS_ID } from '~/utils/tracking/googleAds';
import { isEmbedPath } from '~/data/tool-embeds.mjs';

let reloadScheduled = false;

export default defineNuxtPlugin(() => {
  if (!import.meta.client) return;
  // Embedded calculators run inside other sites' iframes: no banner and no tags there.
  if (isEmbedPath(window.location.pathname)) return;

  const { consent, adConsent, load } = useCookieConsent();
  load();

  const w = window as unknown as { dataLayer: unknown[]; w369AnalyticsLayer: unknown[]; gtag?: (...args: unknown[]) => void };
  w.dataLayer = w.dataLayer || [];
  let analyticsStarted = false;
  function analyticsGtag(..._args: unknown[]) {
    if (!sendGoogleTagToWorker(arguments)) w.w369AnalyticsLayer.push(arguments);
  }
  function nativeGtag(..._args: unknown[]) { w.dataLayer.push(arguments); }
  // gtag.js only honors Arguments objects on the dataLayer, not plain arrays.
  function gtag(..._args: unknown[]) {
    // eslint-disable-next-line prefer-rest-params
    nativeGtag(..._args);
    const destination = (_args[2] as { send_to?: string } | undefined)?.send_to;
    // Ads conversions must leave promptly on signup; keep them on main. GA4
    // gets consent changes and general analytics events on its separate queue.
    if (analyticsStarted && (_args[0] === 'consent' ||
      (_args[0] === 'event' && (!destination || destination.startsWith('G-'))))) {
      analyticsGtag(..._args);
    }
  }

  const initial = consent.value;
  gtag('consent', 'default', {
    analytics_storage: initial?.analytics ? 'granted' : 'denied',
    ad_storage: adConsent.value === 'granted' ? 'granted' : 'denied',
    ad_user_data: adConsent.value === 'granted' ? 'granted' : 'denied',
    ad_personalization: adConsent.value === 'granted' ? 'granted' : 'denied',
  });

  const inject = (attrs: Record<string, string>, children?: string) => {
    const script = document.createElement('script');
    for (const [key, value] of Object.entries(attrs)) {
      script.setAttribute(key, value === '' ? key : value);
    }
    if (children) script.textContent = children;
    document.head.appendChild(script);
  };

  const configureGoogleTag = createGoogleTagLoader(
    (id) => {
      // Signup and CTA tracking call the public gtag function after consent.
      w.gtag = gtag;
      inject({ src: `https://www.googletagmanager.com/gtag/js?id=${id}`, async: '' });
    },
    nativeGtag,
  );

  // Microsoft Clarity ConsentV2 API:
  // https://learn.microsoft.com/en-us/clarity/setup-and-installation/consent-management
  // Clarity is analytics only here — the cookie and privacy policies describe
  // it that way, and the site has no Microsoft Ads to share data with, so
  // ad_Storage stays denied regardless of the visitor's marketing consent.
  const clarityConsent = () => {
    const w2 = window as unknown as { clarity?: (...args: unknown[]) => void };
    w2.clarity?.('consentv2', {
      analytics_Storage: consent.value?.analytics ? 'granted' : 'denied',
      ad_Storage: 'denied',
    });
  };

  const scheduleReload = () => {
    if (reloadScheduled) return;
    reloadScheduled = true;
    window.setTimeout(() => window.location.reload(), 300);
  };

  const loaded = { analytics: false, marketing: false };

  const loadAnalytics = () => {
    if (loaded.analytics) return;
    loaded.analytics = true;
    gtag('consent', 'update', { analytics_storage: 'granted' });

    // GA4 (website analytics)
    analyticsStarted = true;
    w.w369AnalyticsLayer = w.dataLayer.filter(command => (command as IArguments)?.[0] === 'consent');
    w.gtag = gtag;
    analyticsGtag('js', new Date());
    injectGoogleTag('G-QY28MZBWVB', !isRedirectPath(window.location.pathname));
    analyticsGtag('config', 'G-QY28MZBWVB');

    // Microsoft Clarity
    inject(
      {},
      `(function(c,l,a,r,i,t,y){
  c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
  t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i+"?ref=bwt";
  y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
})(window, document, "clarity", "script", "xfofsjg0x3");`,
    );
    // The inline stub above makes window.clarity queue calls immediately.
    clarityConsent();

    // Ahrefs analytics
    inject({
      src: 'https://analytics.ahrefs.com/analytics.js',
      async: '',
      'data-key': 'N9KFWcSEkZHaJkmFAVRLkA',
    });
  };

  const loadMarketing = () => {
    if (loaded.marketing) return;
    loaded.marketing = true;
    gtag('consent', 'update', {
      ad_storage: 'granted',
      ad_user_data: 'granted',
      ad_personalization: 'granted',
    });

    // Rewardful (affiliate tracking)
    inject(
      {},
      `(function(w,r){w._rwq=r;w[r]=w[r]||function(){(w[r].q=w[r].q||[]).push(arguments)}})(window,'rewardful');`,
    );
    inject({ src: 'https://r.wdfl.co/rw.js', async: '', 'data-rewardful': 'e17d4e' });

    // Google Ads tags — the nuxt-gtag module stays disabled because it loads the
    // library before consent. AW-1001723992 is the older tag (March 2024);
    // GOOGLE_ADS_ID is Workspace369's own account (utils/tracking/googleAds.ts).
    // The Ads destinations share one native loader; GA4 has a worker queue.
    configureGoogleTag('AW-1001723992');
    configureGoogleTag(GOOGLE_ADS_ID);

    // Meta Pixel (dataset "Workspace369 Website & Trials"). The inline script
    // runs synchronously, so window.fbq exists right after inject(). Disabling
    // pushState tracking keeps plugins/meta-pixel-pageviews.client.ts as the
    // only source of SPA PageViews.
    inject(
      {},
      `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');
fbq.disablePushState=true;
fbq('init','${META_PIXEL_ID}');
fbq('track','PageView');`,
    );
    window.__w369MetaRevoked = false;
    if (isPricingPath(window.location.pathname)) {
      window.fbq?.('track', 'ViewContent', { content_name: 'pricing' });
    }
  };

  const apply = () => {
    const record = consent.value;

    if (record?.analytics) {
      loadAnalytics();
    } else if (loaded.analytics) {
      gtag('consent', 'update', { analytics_storage: 'denied' });
      clarityConsent();
      scheduleReload();
    }

    if (adConsent.value === 'granted') {
      loadMarketing();
    } else if (loaded.marketing) {
      window.fbq?.('consent', 'revoke');
      window.__w369MetaRevoked = true;
      gtag('consent', 'update', { ad_storage: 'denied', ad_user_data: 'denied', ad_personalization: 'denied' });
      scheduleReload();
    }
  };

  // Schedule consented tags during idle time; third-party execution still has a cost.
  const kickoff = () => {
    apply();
    watch(consent, apply, { deep: true });
  };
  const onIdle = (cb: () => void) =>
    window.requestIdleCallback ? window.requestIdleCallback(cb, { timeout: 3000 }) : window.setTimeout(cb, 1500);

  // The ad redirect hops are the exception: they carry no content to protect and leave within
  // ~1.5s, which is shorter than the idle timeout above, so deferring here meant the Pixel they
  // exist to fire had not loaded yet when the page went away. They load at once instead.
  if (isRedirectPath(window.location.pathname)) {
    kickoff();
  } else if (document.readyState === 'complete') {
    onIdle(kickoff);
  } else {
    window.addEventListener('load', () => onIdle(kickoff), { once: true });
  }
});
